// =============================================================================
// Word Clash — Shared Types
// The question bank, rounds/answers, power-ups and Word Clash's gameplay wire
// payloads. Platform types (users, matches, economy, social) come from
// @shared/types; the extensible ones (MatchRejoinPayload, the typed socket
// maps, DashboardMetrics) are extended here. Payload shapes and enum VALUES
// are frozen wire / persisted contract.
// =============================================================================

import type {
  ClientToServerEvents,
  DashboardBreakdownEntry,
  DashboardMetrics,
  MatchRejoinPayload,
  ServerToClientEvents,
} from '@shared/types';
import { WordClashClientEvents, WordClashServerEvents } from './events';

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export enum QuestionType {
  Category = 'category',
  Prefix = 'prefix',
}

export enum Difficulty {
  Easy = 'easy',
  Medium = 'medium',
  Hard = 'hard',
}

/**
 * Per-question content flags. Used by matchmaking to gate questions to a
 * specific language (prefix-* questions are EN orthographic and meaningless
 * in TR), and by the admin quality dashboard to surface review state.
 *
 * Stored as a string[] to keep it cheap to add new flags without schema
 * migrations. Treat the const as the source of truth — the schema accepts
 * any string but only these are referenced in code.
 */
export const QuestionContentFlag = {
  EnOnly: 'en-only',
  TrOnly: 'tr-only',
  NeedsReview: 'needs-review',
} as const;
export type QuestionContentFlag =
  (typeof QuestionContentFlag)[keyof typeof QuestionContentFlag];

/**
 * Server-computed quality metrics surfaced on every Question. Default values
 * apply on insert; the weekly recompute job (B-2.5) overwrites them. None of
 * these are admin-writable — the admin dashboard (B-2.6) reads them only.
 */
export interface QuestionQualityMetrics {
  /** Composite fun score 0-10. Default 5 (neutral). */
  funScore: number;
  /** Cumulative count of `change_question` power-up uses on this question. */
  skipCount: number;
  /** Aggregated 1-5 star ratings from post-match UI (B-4 follow-up). */
  playerRating: { sum: number; count: number };
  /** Running mean of answersGiven across finished rounds. */
  avgAnswersGiven: number;
  /** Running ratio: rounds where the player who got this question won. 0-1. */
  winRateWhenAsked: number;
  /** Running ratio: rounds where the player ran out of time. 0-1. */
  timeoutRateWhenAsked: number;
  /** ISO timestamp of the last admin review. Optional — unset until reviewed. */
  lastReviewedAt?: string;
}

export enum PowerUpType {
  Joker = 'joker',
  TimeBoost = 'time_boost',
  ChangeQuestion = 'change_question',
}

/** Reason an answer submission was rejected. `null` == answer was valid. */
export type RejectionReason =
  | null
  | 'invalid'
  | 'duplicate'
  | 'out_of_turn'
  | 'timeout'
  | 'match_ended'
  // Submitted answer equals the displayed example hint (illustrative only,
  // never scorable). Distinct from 'invalid' so analytics can track how often
  // players type the shown example.
  | 'example_hint';

// ---------------------------------------------------------------------------
// Data Models
// ---------------------------------------------------------------------------

/** Section 5.3 — Question */
export interface Question {
  id: string;
  type: QuestionType;
  promptEn: string;
  promptTr: string;
  validAnswers: string[];
  validAnswersTr?: string[];
  answerAliases?: Record<string, string>; // alias → canonical valid answer
  answerAliasesTr?: Record<string, string>;
  /**
   * Optional example answer shown to BOTH players during the question preview
   * and gameplay to disambiguate what counts as a valid answer (e.g. "apple"
   * for "Name types of fruit"). It is purely illustrative — the server REJECTS
   * it if a player submits it, so it never consumes a scorable answer. Bilingual
   * like the prompt: TR matches read {@link exampleHintTr}, EN matches read this.
   * Absent ⇒ no hint shown for that language (never falls back across languages,
   * same rule as prompt/valid-answer content).
   */
  exampleHint?: string;
  /** Turkish counterpart to {@link exampleHint}. Shown only in TR matches. */
  exampleHintTr?: string;
  difficulty: Difficulty;
  isActive: boolean;
  /**
   * Admin moderation flag. `false` (or missing on legacy rows) means the
   * question is awaiting human review; `true` means an admin has approved
   * or rejected it via the Question Review page. Rejection additionally
   * flips `isActive` to false.
   */
  reviewed?: boolean;
  /**
   * Language/lifecycle tags. Used by matchmaking to filter EN/TR-only
   * questions (e.g., prefix-* are en-only) and by admin tooling. See
   * {@link QuestionContentFlag}.
   */
  contentFlags?: QuestionContentFlag[];
  /** Server-computed quality metrics. See {@link QuestionQualityMetrics}. */
  quality?: QuestionQualityMetrics;
  createdAt: string;
}

/** Section 5.5 — Round */
export interface Round {
  id: string;
  matchId: string;
  roundNumber: number;
  questionId: string;
  winnerId: string | null;
  player1Points: number;
  player2Points: number;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number;
}

/** Section 5.6 — Answer */
export interface Answer {
  id: string;
  roundId: string;
  userId: string;
  answerText: string;
  isValid: boolean;
  pointsAwarded: number;
  validationMethod: 'exact' | 'fuzzy' | 'alias' | 'token-alias' | 'reverse-token' | 'variant' | null;
  submittedAt: string;
  turnNumber: number;
}

// ---------------------------------------------------------------------------
// WebSocket Payloads — Client → Server
// ---------------------------------------------------------------------------

export interface AcceptQuestionPayload {
  matchId: string;
  roundId: string;
}

export interface ChangeQuestionPayload {
  matchId: string;
  roundId: string;
}

export interface SubmitAnswerPayload {
  matchId: string;
  roundId?: string;
  answerText: string;
}

export interface UseJokerPayload {
  matchId: string;
  roundId?: string;
}

export interface UseTimeBoostPayload {
  matchId: string;
  roundId?: string;
}

// ---------------------------------------------------------------------------
// WebSocket Payloads — Server → Client
// ---------------------------------------------------------------------------

export interface RoundStartPayload {
  roundNumber: number;
  roundId: string;
  question: {
    id: string;
    type: QuestionType;
    prompt: string; // localized prompt
    /** Localized example answer (illustrative only — see {@link Question.exampleHint}). Omitted when none authored for the match language. */
    exampleHint?: string;
  };
  countdown: number;
}

/**
 * Turn-ownership events are self-describing (#523).
 *
 * They carry the round + turn they refer to so the client can drop a stale or
 * out-of-order event instead of latching `isYourTurn` from it, and they carry
 * the server's remaining time so the countdown anchors to the real deadline
 * rather than to packet-arrival time (which silently grants a full turn's
 * worth of time after a delivery stall).
 */
export interface TurnContext {
  /** Round this turn belongs to. Used to reject events from a finished round. */
  roundNumber: number;
  /** Monotonic turn index within the round. Used to reject out-of-order events. */
  turnNumber: number;
  /** Milliseconds left on the server's turn deadline at emit time. */
  remainingMs: number;
}

export type YourTurnPayload = TurnContext;

export type OpponentTurnPayload = TurnContext;

export interface AnswerResultPayload {
  answerText: string;
  isValid: boolean;
  isSpecial?: boolean;
  player: 'you' | 'opponent';
}

export interface CrushedItPayload {
  player: 'you' | 'opponent';
}

export interface RoundEndPayload {
  roundNumber: number;
  yourPoints: number;
  opponentPoints: number;
  winner: 'you' | 'opponent' | 'draw';
}

export interface TimeExtendedPayload {
  newEndTime: string; // ISO timestamp
  player: 'you' | 'opponent';
}

export interface JokerUsedPayload {
  player: 'you' | 'opponent';
  answerText: string;
}

export interface QuestionChangedPayload {
  question: {
    id: string;
    type: QuestionType;
    prompt: string;
    /** Localized example answer (illustrative only). Omitted when none authored for the match language. */
    exampleHint?: string;
  };
  /**
   * Perspective-swapped attribution (#539): true for the player who initiated
   * the change, false for the opponent. Optional for backward compat — an older
   * server omits it and the client falls back to the generic "Question changed!"
   * copy.
   */
  changedByYou?: boolean;
}

/**
 * `match_rejoin` as Word Clash sends it: the platform snapshot plus the live
 * round — question, round points, the answer feed and this round's power-up
 * counts.
 */
export interface WordClashMatchRejoinPayload extends MatchRejoinPayload {
  roundNumber: number;
  roundId: string;
  questionId: string;
  questionPrompt: string;
  /** Localized example answer (illustrative only). Omitted when none authored for the match language. */
  exampleHint?: string;
  yourPoints: number;
  opponentPoints: number;
  answers: AnswerResultPayload[];
  jokerCountThisRound: number;
  timeBoostCountThisRound: number;
}

// ---------------------------------------------------------------------------
// Typed Socket.IO Event Maps (platform maps + Word Clash gameplay)
// ---------------------------------------------------------------------------

/** Server → Client event signatures, platform events included. */
export interface WordClashServerToClientEvents extends Omit<ServerToClientEvents, 'match_rejoin'> {
  [WordClashServerEvents.ROUND_START]: (payload: RoundStartPayload) => void;
  [WordClashServerEvents.YOUR_TURN]: (payload: YourTurnPayload) => void;
  [WordClashServerEvents.OPPONENT_TURN]: (payload: OpponentTurnPayload) => void;
  [WordClashServerEvents.ANSWER_RESULT]: (payload: AnswerResultPayload) => void;
  [WordClashServerEvents.CRUSHED_IT]: (payload: CrushedItPayload) => void;
  [WordClashServerEvents.ROUND_END]: (payload: RoundEndPayload) => void;
  [WordClashServerEvents.TIME_EXTENDED]: (payload: TimeExtendedPayload) => void;
  [WordClashServerEvents.JOKER_USED]: (payload: JokerUsedPayload) => void;
  [WordClashServerEvents.QUESTION_CHANGED]: (payload: QuestionChangedPayload) => void;
  match_rejoin: (payload: WordClashMatchRejoinPayload) => void;
}

/** Client → Server event signatures, platform events included. */
export interface WordClashClientToServerEvents extends ClientToServerEvents {
  [WordClashClientEvents.ACCEPT_QUESTION]: (payload: AcceptQuestionPayload) => void;
  [WordClashClientEvents.CHANGE_QUESTION]: (payload: ChangeQuestionPayload) => void;
  [WordClashClientEvents.SUBMIT_ANSWER]: (payload: SubmitAnswerPayload) => void;
  [WordClashClientEvents.USE_JOKER]: (payload: UseJokerPayload) => void;
  [WordClashClientEvents.USE_TIME_BOOST]: (payload: UseTimeBoostPayload) => void;
}

// ---------------------------------------------------------------------------
// Admin — question bank (POST/GET/PATCH /admin/questions...)
// ---------------------------------------------------------------------------

/**
 * POST /admin/questions request body. Only `type`, `promptEn`, `promptTr`,
 * `validAnswers`, and `difficulty` are required — the rest default on the
 * server (empty arrays / empty maps / category='general'). Every field
 * listed here is also patchable via `PATCH /admin/questions/:id`.
 */
export interface CreateQuestionRequest {
  type: QuestionType;
  promptEn: string;
  promptTr: string;
  validAnswers: string[];
  validAnswersTr?: string[];
  difficulty: Difficulty;
  category?: string;
  specialAnswers?: string[];
  specialAnswersTr?: string[];
  plausibleWrongAnswers?: string[];
  /**
   * Turkish counterpart to `plausibleWrongAnswers`. Used by the bot's
   * `nearMiss` wrong-answer strategy in TR matches. Authors MUST provide
   * Turkish strings here — leaving English values would surface them as
   * wrong answers to TR players. Empty array is acceptable; the bot then
   * falls back to misspellings of `validAnswersTr`.
   */
  plausibleWrongAnswersTr?: string[];
  /** alias → canonical (both trimmed, keys lowercased server-side). */
  answerAliases?: Record<string, string>;
  answerAliasesTr?: Record<string, string>;
  /** Optional illustrative example answer (EN). See {@link Question.exampleHint}. */
  exampleHint?: string;
  /** Optional illustrative example answer (TR). See {@link Question.exampleHintTr}. */
  exampleHintTr?: string;
  /**
   * Optional language/lifecycle flags. Quality metrics ({@link QuestionQualityMetrics})
   * are server-computed and NOT admin-writable on create.
   */
  contentFlags?: QuestionContentFlag[];
}

/**
 * One row in the paginated admin questions list. Mirrors the full persisted
 * `Question` doc so admins can audit every field — aliases, specialAnswers,
 * plausibleWrongAnswers. The list endpoint is paginated so payload size is
 * bounded. The edit form (`/questions/:id`) now edits every admin-writable
 * field — prompts, valid/special/plausible-wrong answers (EN+TR), aliases
 * (EN+TR), difficulty, category, contentFlags, and isActive. `type` is fixed
 * after creation and the server-computed quality metrics are read-only.
 */
export interface QuestionListItem {
  id: string;
  type: QuestionType;
  promptEn: string;
  promptTr: string;
  validAnswers: string[];
  validAnswersTr: string[];
  /** alias → canonical valid answer (both fields' aliases flattened to objects on the wire). */
  answerAliases: Record<string, string>;
  answerAliasesTr: Record<string, string>;
  category: string;
  specialAnswers: string[];
  specialAnswersTr: string[];
  plausibleWrongAnswers: string[];
  plausibleWrongAnswersTr: string[];
  /** Illustrative example answers (EN/TR). Empty string ⇒ none authored. */
  exampleHint: string;
  exampleHintTr: string;
  difficulty: Difficulty;
  isActive: boolean;
  /**
   * Admin moderation flag. Missing on the wire for legacy rows that haven't
   * been touched since this field was introduced — clients MUST treat
   * `undefined` as "not yet reviewed".
   */
  reviewed?: boolean;
  contentFlags: QuestionContentFlag[];
  quality: QuestionQualityMetrics;
  createdAt: string;
}

/**
 * Response shape of POST /admin/questions/:id/approve and /reject.
 * Extends the standard item with a `changed` flag so the client can
 * distinguish "we wrote a new state" from "already in target state".
 */
export interface QuestionReviewResponse extends QuestionListItem {
  changed: boolean;
}

// ---------------------------------------------------------------------------
// AI question authoring assistant (admin-only) — POST /admin/questions/assist
// ---------------------------------------------------------------------------

/**
 * One turn in the assistant chat history. `assistant` turns are the model's
 * prior replies (the human-readable `reply` text only, never the JSON patch).
 */
export interface QuestionAssistTurn {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Editable subset of a question the assistant may propose. Every field is
 * optional — the model returns only what it changed, and the admin reviews
 * the diff before applying it into the form. Mirrors the admin-writable
 * fields on {@link CreateQuestionRequest}; server-computed quality metrics
 * and the moderation flags (`reviewed`, `isActive`) are intentionally absent.
 */
export interface QuestionAssistPatch {
  promptEn?: string;
  promptTr?: string;
  validAnswers?: string[];
  validAnswersTr?: string[];
  specialAnswers?: string[];
  specialAnswersTr?: string[];
  plausibleWrongAnswers?: string[];
  plausibleWrongAnswersTr?: string[];
  answerAliases?: Record<string, string>;
  answerAliasesTr?: Record<string, string>;
  difficulty?: Difficulty;
  category?: string;
  contentFlags?: QuestionContentFlag[];
}

/**
 * Request body for the AI authoring assistant. `instruction` is the admin's
 * latest chat message; `draft` is the current state of the form (so the model
 * edits in place rather than starting from scratch); `history` carries prior
 * turns for multi-message conversations.
 */
export interface QuestionAssistRequest {
  instruction: string;
  draft: QuestionAssistPatch;
  history?: QuestionAssistTurn[];
}

/**
 * Assistant reply. `reply` is the human-readable chat message rendered in the
 * panel. `suggestion` (when present) is a proposed patch the admin can apply
 * into the form with one click; it is NEVER auto-persisted — saving still goes
 * through the normal validated PATCH/POST path.
 */
export interface QuestionAssistResponse {
  reply: string;
  suggestion?: QuestionAssistPatch;
}

/** GET /admin/questions response envelope. */
export interface QuestionsListResponse {
  data: QuestionListItem[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

// ---------------------------------------------------------------------------
// Admin dashboard metrics — Word Clash section
// ---------------------------------------------------------------------------

/** Question-bank metrics. `unreviewed` mirrors the Question Review queue query. */
export interface DashboardQuestionMetrics {
  total: number;
  active: number;
  /**
   * Active questions playable in EN / TR matches — same filter the game's
   * question selection uses (`buildLanguageFilter`), so these reflect the
   * actual per-language pool size, not just prompt presence. A bilingual
   * question counts in both.
   */
  activeEn: number;
  activeTr: number;
  inactive: number;
  /** Rows still awaiting moderation (`reviewed != true`) — the Review queue size. */
  unreviewed: number;
  byType: DashboardBreakdownEntry[];
  byDifficulty: DashboardBreakdownEntry[];
}

/** `GET /api/admin/dashboard/metrics` with Word Clash's question-bank section. */
export interface WordClashDashboardMetrics extends DashboardMetrics {
  questions: DashboardQuestionMetrics;
}

// ---------------------------------------------------------------------------
// Admin match view — rounds (GET /api/admin/matches/:id/rounds)
// ---------------------------------------------------------------------------

/**
 * One answer submission inside an admin round view. Mirrors the persisted
 * `Answer` doc with `userId` expanded to `{id, displayName}` for display.
 * `turnNumber` is the turn on which the answer was submitted (1-indexed
 * within the round).
 */
export interface AdminMatchAnswer {
  id: string;
  userId: string;
  userDisplayName: string;
  answerText: string;
  normalizedText: string;
  isValid: boolean;
  pointsAwarded: number;
  validationMethod: 'exact' | 'fuzzy' | 'alias' | 'token-alias' | 'reverse-token' | 'variant' | null;
  rejectionReason: RejectionReason;
  turnNumber: number;
  submittedAt: string; // ISO timestamp
}

/**
 * One round inside an admin match view. Rounds are ordered by `roundNumber`
 * ascending. `question` is the prompt+type at the time the round was played
 * — surfaced here so admins don't have to cross-reference `Question` docs
 * to understand what was asked.
 */
export interface AdminMatchRound {
  id: string;
  roundNumber: number;
  question: {
    id: string;
    type: QuestionType;
    promptEn: string;
    promptTr: string;
    difficulty: Difficulty;
  };
  winnerId: string | null;
  player1Points: number;
  player2Points: number;
  player1JokerCount: number;
  player2JokerCount: number;
  player1TimeBoostCount: number;
  player2TimeBoostCount: number;
  player1ChangeQuestionCount: number;
  player2ChangeQuestionCount: number;
  startedAt: string; // ISO timestamp
  endedAt: string | null; // ISO timestamp
  answers: AdminMatchAnswer[];
}

/** Response for GET /api/admin/matches/:id/rounds — ordered by roundNumber asc. */
export interface AdminMatchRoundsResponse {
  matchId: string;
  rounds: AdminMatchRound[];
}
